import React, { useState } from 'react';
import axios from 'axios';
import { Camera, CheckCircle2, Loader2, UserPlus } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', phone: '', place: '', occupation: '', additional_info: '', photo: '' };

const PhotoField = ({ value, onChange }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return setErr(t('membership.photoTooLarge'));
    setBusy(true); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/membership/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.photo);
    } catch { setErr(t('membership.photoFailed')); } finally { setBusy(false); }
  };
  return (
    <div className="jbss-member-photo-upload">
      <label className="jbss-app-photo" data-testid="membership-photo-preview">
        {value ? <img src={`${API}/photos/${value}`} alt="" /> : <Camera size={20} />}
        <input type="file" hidden accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => { upload(e.target.files && e.target.files[0]); e.target.value = ''; }} data-testid="membership-photo-input" />
      </label>
      <div className="jbss-donate-hint">
        {busy ? <span><Loader2 className="jbss-spin" size={12} /> {t('membership.uploading')}</span> : t('membership.photoHint')}
        {value && !busy && <button type="button" className="jbss-admin-link" onClick={() => onChange('')} data-testid="membership-photo-remove"> {t('membership.photoRemove')}</button>}
        {err && <p className="jbss-donate-error" data-testid="membership-photo-error">{err}</p>}
      </div>
    </div>
  );
};

const Field = ({ id, label, children }) => (
  <>
    <Label htmlFor={id} className="jbss-donate-label">{label}</Label>
    {children}
  </>
);

export const MembershipDialog = ({ open, onOpenChange }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value }));

  const change = (v) => { if (!v) { setDone(false); setError(''); } onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.place.trim()) return setError(t('membership.required'));
    if (!/^[6-9]\d{9}$/.test(form.phone)) return setError(t('donation.invalidPhone'));
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/membership`, { ...form, photo: form.photo || null, lang });
      setDone(true); setForm(EMPTY);
    } catch (err) {
      setError(err?.response?.status === 409 ? t('membership.duplicate') : t('membership.failed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="jbss-donate-dialog" data-testid="membership-dialog">
        <DialogHeader>
          <DialogTitle className="jbss-donate-title">{done ? t('membership.successTitle') : t('membership.title')}</DialogTitle>
          <DialogDescription className="jbss-donate-sub">{done ? t('membership.successMsg') : t('membership.subtitle')}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="jbss-donate-success" data-testid="membership-success">
            <CheckCircle2 size={48} className="jbss-donate-success-icon" />
            <button type="button" className="jbss-admin-link" onClick={() => change(false)} data-testid="membership-close">{t('donation.close')}</button>
          </div>
        ) : (
          <form className="jbss-donate-form" onSubmit={submit} noValidate data-testid="membership-form">
            <Field id="m-name" label={`${t('membership.name')} *`}><Input id="m-name" maxLength={80} value={form.name} onChange={setF('name')} data-testid="membership-name" /></Field>
            <Field id="m-phone" label={`${t('donation.phone')} *`}>
              <div className="jbss-donate-phone"><span>+91</span>
                <Input id="m-phone" inputMode="numeric" maxLength={10} placeholder="98XXXXXXXX" value={form.phone} onChange={setF('phone')} data-testid="membership-phone" />
              </div>
            </Field>
            <Field id="m-place" label={`${t('membership.place')} *`}><Input id="m-place" maxLength={80} value={form.place} onChange={setF('place')} data-testid="membership-place" /></Field>
            <Field id="m-occ" label={t('membership.occupation')}><Input id="m-occ" maxLength={80} value={form.occupation} onChange={setF('occupation')} data-testid="membership-occupation" /></Field>
            <Field id="m-info" label={t('membership.additional')}><Textarea id="m-info" maxLength={1000} value={form.additional_info} onChange={setF('additional_info')} data-testid="membership-additional" /></Field>
            <Label className="jbss-donate-label">{t('membership.photo')}</Label>
            <PhotoField value={form.photo} onChange={(p) => setForm((f) => ({ ...f, photo: p }))} />
            {error && <p className="jbss-donate-error" data-testid="membership-error">{error}</p>}
            <button type="submit" className="jbss-btn jbss-btn-navy jbss-donate-submit" disabled={busy} data-testid="membership-submit">
              {busy ? <Loader2 className="jbss-spin" size={16} /> : <UserPlus size={16} />}
              <span>{t('membership.submit')}</span>
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
